import { ScheduleEntry, VesselStatusSchedule, VesselStatusActual } from '../../src/models/types/voyage-schedules-model.types'
import { VoyagePlan } from '../../src/models/vessel-planning-model'
import { VesselPositionStoredEntry } from '../../src/models/vessel-position-model-impl'
import { getDatabase } from '../db_manager'
import { PortMemoListType, TerminalInfo } from '../../src/models/types/terminals-model.types';
import { BprAPIResponse, BprRecord } from '../../src/models/types/voyage-best-practice-routes-impl.types';
import { CharacteristicsWithIMO } from '../../src/services/vessel-plans-service';
import { ZoneStoreEntry } from '../db_manager';
import { ZoneFeature } from '../../src/models/zones-base-interfaces';
import { firstUncloneablePath, isDataCloneError, toStorableCopy } from '../../src/utils/structured-clone-utils';

/**
 * Writes a plan to the cache, and keeps the cache alive when the plan carries something IndexedDB
 * cannot store.
 *
 * A plan is plain data by design, but one non-plain value left on it by another code path (a fetch
 * Response parked on a route after a failed request is enough) fails the whole write with a
 * DataCloneError - and the plan then silently stops being cached, which surfaces much later as a
 * stale plan offline. The write is retried once with a storable copy so the cache survives, and the
 * offending property is logged by path so the real source can be fixed.
 */
async function cachePlan<TPlan, TResult>(write: (cacheable: TPlan) => Promise<TResult>, plan: TPlan, operation: string): Promise<TResult> {
    try {
        return await write(plan);
    } catch (error) {
        if (!isDataCloneError(error)) {
            throw error;
        }

        console.error(`[IndexedDB] plans.${operation}: the plan holds a value IndexedDB cannot store, caching a plain copy instead. Offending: ${firstUncloneablePath(plan, 'plan')}`, error);
        return await write(toStorableCopy(plan));
    }
}

export class IndexedDBService {
    static plans = {
        async getAll(): Promise<VoyagePlan[]> {
            return await getDatabase().plan.getAllPlans()
        },
        async get(id: number) {
            return await getDatabase().plan.getPlan(id)
        },
        async getByScheduleIds(scheduleIds: string[]) {
            return await getDatabase().plan.getPlanByScheduleIds(scheduleIds)
        },
        async add(plan: VoyagePlan) {
            return await cachePlan(cacheable => getDatabase().plan.addPlan(cacheable), plan, 'add')
        },
        async addAll(plans: VoyagePlan[]) {
            return await cachePlan(cacheable => getDatabase().plan.addAll(cacheable), plans, 'addAll')
        },
        async update(id: number, plan: VoyagePlan) {
            return await cachePlan(cacheable => getDatabase().plan.updatePlan(id, cacheable), plan, 'update')
        },
        async updatePlanByScheduleId(scheduleId: string, plan: VoyagePlan) {
            return await cachePlan(
                cacheable => getDatabase().plan.updatePlanByScheduleId(scheduleId, cacheable), plan, 'updatePlanByScheduleId')
        },
        async delete(scheduleId: string) {
            return await getDatabase().plan.deletePlan(scheduleId)
        },
    }

    static schedules = {
        async getAll() {
            return await getDatabase().schedule.getAllSchedules()
        },
        async get(id: string) {
            return await getDatabase().schedule.getSchedule(id)
        },
        async add(schedule: ScheduleEntry) {
            return await getDatabase().schedule.addSchedule(schedule)
        },
        async update(id: string, schedule: ScheduleEntry) {
            return await getDatabase().schedule.updateSchedule(id, schedule)
        },
        async delete(id: string) {
            return await getDatabase().schedule.deleteSchedule(id)
        },
    }

    static vesselStatus = {
        async get() {
            return await getDatabase().vesselStatus.getVesselStatus()
        },
        async add(schedule: VesselStatusSchedule) {
            return await getDatabase().vesselStatus.addVesselStatus(schedule)
        },
        async delete(id: string) {
            return await getDatabase().vesselStatus.deleteVesselStatus(id)
        }
    }

    static vesselStatusActual = {
        async get() {
            return await getDatabase().vesselStatusActual.getVesselStatusActual()
        },
        async add(schedule: VesselStatusActual) {
            return await getDatabase().vesselStatusActual.addVesselStatusActual(schedule)
        },
        async delete(id: string) {
            return await getDatabase().vesselStatusActual.deleteVesselStatusActual(id)
        }
    }

    static position = {
        async get() {
            return await getDatabase().position.getPosition()
        },
        async add(position: VesselPositionStoredEntry) {
            return await getDatabase().position.addPosition(position)
        },
        async update(position: VesselPositionStoredEntry) {
            return await getDatabase().position.updatePosition(position)
        },
        async delete() {
            return await getDatabase().position.deletePosition()
        },
    }

    static notifications = {
        async getAll() {
            return await getDatabase().notification.getAllNotifications()
        },

        async get(notificationId: string | number) {
            return await getDatabase().notification.getNotification(notificationId)
        },

        async add(response: any) {
            return await getDatabase().notification.addNotification(response)
        },

        async update(
            notificationId: string | number,
            response: any
        ) {
            return await getDatabase().notification.updateNotification(notificationId, response)
        },

        async delete(notificationId: string | number) {
            return await getDatabase().notification.deleteNotification(notificationId)
        },

        async deleteAll() {
            return await getDatabase().notification.deleteAllNotifications()
        }
    }

    static terminals = {
        async getAll(): Promise<TerminalInfo[]> {
            return await getDatabase().terminal.getAllTerminals()
        },
        async add(terminal: TerminalInfo) {
            return await getDatabase().terminal.addTerminal(terminal)
        },
        async get(terminal: string) {
            return await getDatabase().terminal.getTerminal(terminal)
        },
    }

    static batchTimeToLocal = {
        async get(inputTime: string) {
            return await getDatabase().batchTimeToLocal.getConvertedTime(inputTime)
        },
        async getAll() {
            return await getDatabase().batchTimeToLocal.getTerminalTimeToLocal();
        },
        async addAll(timeObjArray: []) {
            return await getDatabase().batchTimeToLocal.addTerminalTimeToLocal(timeObjArray)
        }
    }

    static vesselCharacteristics = {
        async get() {
            return await getDatabase().vesselCharacteristics.getVesselCharacteristics()
        },
        async add(vesselCharacteristics: CharacteristicsWithIMO) {
            return await getDatabase().vesselCharacteristics.addVesselCharacteristics(vesselCharacteristics)
        },
    }

    static bprs = {
        async add(bpr: BprRecord) {
            return await getDatabase().bprs.addBprs(bpr);
        },
        async update(id: string, bpr: BprAPIResponse){
            return await getDatabase().bprs.updateBprs(id, bpr);
        },
        async getAll(): Promise<BprRecord[]>{
            return await getDatabase().bprs.getAllBprs()
        },
        async get(schedule: string) {
            return await getDatabase().bprs.getScheduleBprs(schedule);
        },
        async delete(id: string){
            return await getDatabase().bprs.deleteBpr(id);
        },
        async deleteAll(){
            return await getDatabase().bprs.deleteAll();
        }
    }

    static oors = {
        async add(oor: BprRecord) {
            return await getDatabase().oors.addOors(oor);
        },
        async getAll(): Promise<BprRecord[]> {
            return await getDatabase().oors.getAllOors();
        },
        async get(scheduleId: string) {
            return await getDatabase().oors.getScheduleOors(scheduleId);
        },
        async delete(id: string) {
            return await getDatabase().oors.deleteOor(id);
        },
        async deleteAll() {
            return await getDatabase().oors.deleteAll();
        }
    }

    static portMemos = {
        async get(terminalId: string) {
            return await getDatabase().portMemos.getPortMemo(terminalId);
        },
        async getAll() {
            return await getDatabase().portMemos.getAllPortMemos();
        },
        async addAll(portMemosArray:  PortMemoListType[]) {
            return await getDatabase().portMemos.addAllPortMemos(portMemosArray);
        }
    }

    static zones = {
        async getByType(zoneType: string): Promise<ZoneFeature[] | null> {
            try {
                const entry = await getDatabase().zones.getZonesByType(zoneType);
                if (entry?.zonesData) {
                    const zones = JSON.parse(entry.zonesData) as ZoneFeature[];
                    return zones;
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        async save(zoneType: string, zones: ZoneFeature[]) {
            try {
                const entry: ZoneStoreEntry = {
                    zoneType,
                    zonesData: JSON.stringify(zones),
                    lastUpdated: new Date().toISOString(),
                };
                await getDatabase().zones.addZones(entry);
            } catch (error) {
                console.error(`[IndexedDB] zones.save('${zoneType}') failed:`, error);
            }
        },
        async deleteByType(zoneType: string) {
            return await getDatabase().zones.deleteZonesByType(zoneType);
        },
        async deleteAll() {
            return await getDatabase().zones.deleteAllZones();
        }
    }
}
